'use client';

import { FormEvent, useState } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { PublicShell } from '@/components/layout/PublicShell';

export default function LoginPage() {
  const router = useRouter();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [unverified, setUnverified] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendMessage, setResendMessage] = useState('');

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    setLoading(true);
    setMessage('');
    setUnverified(false);

    try {
      const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.message === 'REGISTRATION_PENDING_APPROVAL') {
          throw new Error(
            'A regisztrációd jóváhagyásra vár. Amint jóváhagytuk, emailben küldjük a megerősítő linket.',
          );
        }

        if (data.message === 'EMAIL_NOT_VERIFIED') {
          setUnverified(true);
          throw new Error('Az email címed még nincs megerősítve.');
        }

        throw new Error(data.message || 'Sikertelen belépés');
      }

      localStorage.setItem('accessToken', data.accessToken);
      localStorage.setItem('currentUser', JSON.stringify(data.user));
      switch (data.user.onboardingStage) {
        case 'basic':
          router.push('/profile/onboarding');
          break;
        case 'required_attributes':
          router.push('/profile/onboarding/required');
          break;
        case 'optional_attributes':
          router.push('/profile/onboarding/optional');
          break;
        case 'completed':
          router.push('/dashboard');
          break;
      }
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Ismeretlen hiba');
    } finally {
      setLoading(false);
    }
  }

  async function handleResend() {
    setResending(true);
    setResendMessage('');

    try {
      await fetch(`${process.env.NEXT_PUBLIC_API_URL}/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      setResendMessage('Ha az email cím regisztrálva van, elküldtük az új megerősítő linket.');
    } finally {
      setResending(false);
    }
  }

  return (
    <PublicShell>
      <main className="min-h-screen flex items-center justify-center p-6">
        <form
          onSubmit={handleSubmit}
          className="w-full max-w-md rounded-2xl border p-6 space-y-4 shadow-sm"
        >
          <h1 className="text-2xl font-bold">Belépés</h1>

          <label className="block space-y-1">
            <span className="text-sm font-medium">Email</span>
            <input
              className="w-full rounded-xl border px-3 py-2"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
            />
          </label>

          <label className="block space-y-1">
            <span className="text-sm font-medium">Jelszó</span>
            <input
              className="w-full rounded-xl border px-3 py-2"
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
            />
          </label>

          <button
            type="submit"
            disabled={loading}
            className="w-full rounded-xl bg-primary text-primary-foreground px-4 py-2 font-medium disabled:opacity-60"
          >
            {loading ? 'Belépés...' : 'Belépek'}
          </button>

          {message && <p className="rounded-xl bg-muted px-3 py-2 text-sm text-foreground">{message}</p>}

          <Link
            href="/forgot-password"
            className="block text-center text-sm text-muted-foreground underline"
          >
            Elfelejtettem a jelszavam
          </Link>

          {unverified && (
            <div className="space-y-2">
              <button
                type="button"
                onClick={handleResend}
                disabled={resending}
                className="w-full rounded-xl border px-4 py-2 font-medium disabled:opacity-60"
              >
                {resending ? 'Küldés...' : 'Megerősítő email újraküldése'}
              </button>

              {resendMessage && (
                <p className="rounded-xl bg-muted px-3 py-2 text-sm text-foreground">{resendMessage}</p>
              )}
            </div>
          )}
        </form>
      </main>
    </PublicShell>
  );
}
